/**
 * 메뉴 묶음 — 제품 · 재고 (현민 2026-10-07 → 2026-10-08 세 층으로).
 *   메뉴  = 목록 하나(사이드바 한 줄)
 *   탭    = 같은 주어의 다른 면(페이지 위 탭 줄)
 *   새 탭 = 목록의 한 줄을 눌러 여는 상세(`detail`)
 * 시안이 있는 탭은 그 시안으로, 없는 탭은 자리 페이지(MenuStubView)로 — `stub`.
 * `what` 은 그 탭이 하는 일 한 줄. 자리 페이지가 그대로 보여 준다.
 */
import { escapeHtml } from '../lib/cells';

export interface MenuTab {
  label: string;
  href: string;
  what: string;
  /** true = 화면이 아직 없다 — 자리 페이지가 href 에 선다 */
  stub: boolean;
}

export interface MenuItem {
  label: string;
  /** 첫 탭의 주소 — 사이드바가 여는 곳 */
  href: string;
  tabs: MenuTab[];
  /** 줄을 누르면 새 탭에 열리는 상세. null = 상세 없음 */
  detail: string | null;
  buttons: string[];
  /** 사이드바의 빨간 숫자 — 사람이 봐야 할 건수(요청 대기 · 검수 대기 …). null = 안 보임. 고정값(2026-10-08) — 사는 수는 alarm 으로 */
  count: number | null;
  /** 사이드바가 서버에서 세어 오는 알람. null = 알람 없음. 첫 알람 catalog_drafted = 요청함 확인 대기 수(2026-10-14) */
  alarm: EMenuAlarm | null;
}

/** 사이드바 알람의 종류 — 하나마다 세는 함수가 AppSidebar 에 있다 */
export const E_MENU_ALARMS = ['catalog_drafted'] as const;
export type  EMenuAlarm    = typeof E_MENU_ALARMS[number];

export interface MenuGroup {
  title: string;
  icon: string;
  items: MenuItem[];
}

export const MENU_GROUPS: MenuGroup[] = [
  {
    title: '제품', icon: '📋',
    items: [
      {
        label: '제품 목록', href: '/products',
        tabs: [
          { label: '전체',   href: '/products',          what: '회사가 파는 단위(product) 한 줄 — 파트넘버 · 디스크립션 · 카테고리 · 제조사 · 카탈로그 연결 · 재고 수 · 등록자. 실무 제품만(견적이 자동으로 만든 구성은 빠짐)', stub: false },
        ],
        detail: '제품 상세 — 구성(hclt_product_item) · 메모 · 재고 수 · 구매 · 판매 이력',
        buttons: ['제품 등록(PN 하나 · 설명 · 카테고리, PN 없어도 됨. 카탈로그에 있으면 연결만)'],
        count: null,
        alarm: null,
      },
      {
        label: '제품 관리', href: '/products/unlinked',   // 사람이 손볼 제품 — 목록에서 떼어 냄(현민 2026-10-20)
        tabs: [
          { label: '미연결', href: '/products/unlinked', what: '카탈로그 행이 아직 없는 product — 요청이 열려 있다(총괄 시안의 "임시 제품")', stub: false },
          { label: '합칠 후보', href: '/products/merge', what: '같은 물건인데 둘로 갈린 제품의 짝 — 같은 파트넘버 서버 · 스페어 번호로 생긴 제품. 사람이 확인창을 보고 [합치기](2026-10-20)', stub: false },
        ],
        detail: '제품 상세',
        buttons: [],
        count: null,
        alarm: null,
      },
    ],
  },
  {
    title: '카탈로그', icon: '📚',   // 제품처럼 대분류로(현민 2026-10-23) — 카탈로그 · 카탈로그 관리 · 카탈로그 규칙
    items: [
      {
        label: '카탈로그 현황', href: '/catalog-entry/requests',   // 옛 '카탈로그'(현민 2026-10-23). 첫 화면은 요청함(A 안, 현민 2026-10-14) — 제품팀의 할 일이 먼저, 표 브라우저는 검색 탭 안에만
        tabs: [
          { label: '요청',   href: '/catalog-entry/requests', what: '확인 대기(루틴 조사 초안) · 요청됨 · 컨펌 · 반려 — 칸을 채워 승격 · 반려', stub: false },
          { label: '검색',   href: '/catalog',                what: '옛 전체 검색 그대로 — 모델 · SKU · 부품 17표. 29 표 목록은 여기서만', stub: false },
        ],
        detail: '카탈로그 상세(메모 · 바로 등급 수정)',
        buttons: [],
        count: null,
        alarm: 'catalog_drafted',
      },
      {
        label: '카탈로그 관리', href: '/catalog-entry/bulletin',   // 옛 '불레틴'(현민 2026-10-20)
        tabs: [
          { label: '불레틴', href: '/catalog-entry/bulletin', what: '벤더 문서 한 편 = 한 줄. 상태(unpacked … assembled) · 사람 볼 것(attention) 탭', stub: false },
        ],
        detail: '레코드 페이지(조립 결과 줄 단위) · 걸린 줄 페이지(컨펌 · 고쳐서 컨펌 · 보류)',
        buttons: ['다시 검사', 'assemble_error 적기'],
        count: null,
        alarm: null,
      },
      {
        label: '카탈로그 규칙', href: '/catalog-entry/rules',   // 약식 디스크립션 규칙 — 사람이 만들고 고른다(현민 2026-10-23)
        tabs: [
          { label: '규칙', href: '/catalog-entry/rules', what: '카테고리마다 규칙 목록(기본 ★) · 자리 한 줄씩 편집기 · 저장 전 미리보기 · 버전 되돌리기. 고치면 그 규칙을 쓰는 카탈로그 행 · 제품의 약식이 즉시 다시 써진다(영향 수 확인창)', stub: false },
        ],
        detail: '',
        buttons: ['새 규칙', '저장', '기본으로', '지우기', '이 버전으로'],
        count: null,
        alarm: null,
      },
    ],
  },
  {
    title: '재고', icon: '📦',
    items: [
      {
        label: '재고 현황', href: '/preview/inventory',
        tabs: [
          { label: '목록',   href: '/preview/inventory',       what: '구매 줄(로트) 하나 = 한 줄(옛 이름 구매 건 — 2026-10-28) · 필터 [구매일 ▾] · 카테고리, 찾기는 파트넘버 · 디스크립션 · 구매처 — 수량은 그 로트의 입고 · 출고 · 사용 중(빨강) · 사용 가능(초록), 입고 = 출고 + 사용 중 + 사용 가능. 카테고리 · 제조사 · 단가 · 구매일 · 구매처 · 담당자 · 구매번호. 줄을 누르면 그 컨디션으로 재고 상세(새 탭). 품목별 탭을 합쳤다(2026-10-26)', stub: false },
          { label: '실물',   href: '/preview/inventory/units',  what: '실물 한 장 = 한 줄(시리얼 기준) · [작업 ▾](검수 완료 · 이동 · 홀딩 · 풀기 · 자산으로 · 자산 회수 · 분실) · 검색 · 상태 필터', stub: false },
          { label: '이력', href: '/preview/inventory/events', what: '옛 이름 변동표(2026-10-20 — 변동표는 이카운트 꼴 기간 요약으로 따로). 이벤트 한 줄 = 한 줄(inventory_event) — 기간(기본 최근 30일) · 찾기 · 무슨 일 · 사유. 제품 · 업로드로 걸러 연다(?product_id= · ?stocktake_id=). 이카운트 재고수불부의 자리', stub: false },
        ],
        detail: '재고 상세 — 실물 행(시리얼 · 상태 · 구역 · 원가 · 구매처) · 생긴 근거 · 변동 이력 · 구매 · 판매 이력',
        buttons: [],
        count: null,
        alarm: null,
      },
      {
        label: '변동표', href: '/preview/inventory/flows',   // 재고 현황의 탭에서 뺀 메뉴(회의 2026-10-21 §13 — 목적이 달라 따로)
        tabs: [
          { label: '변동표', href: '/preview/inventory/flows', what: '기간 요약(이카운트 재고변동표 꼴) — 제품 × 컨디션 한 줄: 기초재고 · 입고 · 출고 · 기말재고. 기간 프리셋(오늘 · 1주 · 1개월 · 3개월 · 1년). 줄을 누르면 그 제품 × 컨디션의 일별', stub: false },
        ],
        detail: '일별 — 변동표 줄의 제품 × 컨디션을 날짜 하나 = 한 줄로(/preview/inventory/flows/days)',
        buttons: [],
        count: null,
        alarm: null,
      },
      {
        label: '입고', href: '/inventory/receive',
        tabs: [
          { label: '구매 목록', href: '/inventory/receive', what: '구매가 끝나 받을 줄 목록(발주서 한 줄 = 한 행) → [입고 등록] 이 그 줄의 카드를 깔고 재고 등록을 연다', stub: false },
          { label: '재고 등록', href: '/inventory/new',     what: '바코드 스캔 · 구매 품목 카드 · 사진 → 확인 → 채우기 → 저장. 서버 한 대 · 파트 한 장 · 일괄(시트)', stub: false },
          { label: '검수',      href: '/inventory/inspect', what: '검수 전 실물 목록 — 품목 · 수량 · 컨디션 확인 · 테스트 리포트 → 실가용', stub: true },
        ],
        detail: '실물 한 장(구매 줄 · 시리얼 · 사진)',
        buttons: ['구매 줄 고르기', 'PN 직접'],
        count: null,
        alarm: null,
      },
      {
        label: '출고', href: '/inventory/setup',
        tabs: [
          { label: '세팅',        href: '/inventory/setup',             what: '수주 줄 기준 구성 대조(product · 컨디션) → 출고예정. 미검수 실물도 됨 — 세팅 완료가 검수를 겸함', stub: true },
          { label: '출고',        href: '/preview/logistics/dispatch',  what: '출고 전 점검 · 증적(이벤트 + 사진, 상태 없음) → 출고 · 임대 출고', stub: false },
          { label: '반품 · 회수', href: '/inventory/return',            what: '고객 반품 · 임대 회수 → 같은 실물이 검수로. 판매전표는 다시 열림', stub: true },
        ],
        detail: '세팅 — 수주 줄에서 연 구성 페이지(실물 고르기 · 대조)',
        buttons: ['세팅 완료', '출고', '임대 출고'],
        count: null,
        alarm: null,
      },
      {
        label: '실사 · 설정', href: '/inventory/stocktake',
        tabs: [
          { label: '실사 · 조정', href: '/inventory/stocktake', what: '이카운트 자료(품목등록 · 재고현황 · 구매현황) 올리기 → 미리보기 → 반영. 업로드 내역 · 연결 안 된 품목 잇기. 실사 표는 미정', stub: false },
          { label: '필수 재고', href: '/inventory/safety-stock', what: '제품마다 필수 재고(옛 안전재고 — 회의 2026-10-21 §8) — 가용과 견주어 모자란 것이 위. 그 자리에서 고친다. 자리는 제품 · 구매 쪽으로 옮길 예정', stub: false },
          { label: '창고 · 구역', href: '/preview/zones',       what: '창고 · 구역 목록과 카테고리별 기본 자리(warehouse_zone)', stub: false },
        ],
        detail: null,
        buttons: [],
        count: null,
        alarm: null,
      },
    ],
  },
  {
    title: '판매', icon: '🧾',   // 판매 현황 실데이터(백엔드 V장 "판매 공사", 2026-11-02). 시안(이카운트) 탭은 2026-11-03 뺌 — 주소 · 모듈은 11번 때 걷는다
    items: [
      {
        label: '판매 현황', href: '/sales',
        tabs: [
          { label: '판매 현황', href: '/sales', what: '판매 한 건 = 한 줄 — 견적 번호 · 딜 · 고객 · 품목 앞 3줄 · 상태 · 판매 · 원가(묶인 실물) · 마진 · 부족 · 담당자. 탭 진행 중(기본) · 상태 여섯 · 전부. 줄을 누르면 판매 상세(새 탭). 버튼 [출고 요청] · [세팅 완료] · [출고] · [취소]', stub: false },
        ],
        detail: '판매 한 건(/sales/:id) — 머리 · 품목 고치기(감액) · 품목 추가 · 딜 연결 · 이력',
        buttons: [],
        count: null,
        alarm: null,
      },
    ],
  },
];

export interface MenuPlace { group: MenuGroup; item: MenuItem; tab: MenuTab }

/**
 * href 로 탭 하나 — 자리 페이지가 제 글을 찾을 때, 사이드바가 활성을 정할 때.
 * 같은 href 가 먼저, 없으면 그 아래 경로(`/inventory/new/part` → 재고 등록 탭)를 받는 가장 긴 탭.
 */
export function findMenuPlace(href: string): MenuPlace | null {
  let under: MenuPlace | null = null;
  for (const group of MENU_GROUPS) {
    for (const item of group.items) {
      for (const tab of item.tabs) {
        if (tab.href === href) return { group, item, tab };
        const isUnder = href.startsWith(tab.href + '/') && (under === null || tab.href.length > under.tab.href.length);
        if (isUnder) under = { group, item, tab };
      }
    }
  }
  return under;
}

/** 페이지 위 탭 줄 — 그 메뉴의 탭 전부, 지금 자리가 활성. 자리 페이지 · 시안 페이지가 같이 쓴다 */
export function renderMenuTabs(place: MenuPlace): string {
  const tabs = place.item.tabs.map((tab) => `<a class="ic-tab${tab === place.tab ? ' active' : ''}" data-link href="${tab.href}">${escapeHtml(tab.label)}${tab.stub ? ' <span class="app-nav-stub">준비 중</span>' : ''}</a>`).join('');
  return `<nav class="ic-tabs menu-tabs">${tabs}</nav>`;
}

/** 페이지 머리글 — "📦 입고"(메뉴 이름만) */
export function menuHeading(place: MenuPlace): string {
  return `${place.group.icon} ${escapeHtml(place.item.label)}`;   // 묶음 이름은 사이드바가 보여 준다 — 머리글엔 메뉴 이름만(현민 2026-10-14 "중복처럼 보임")
}
